import { useEffect, useState } from 'react';
import { Loader2, ChevronDown, ChevronRight, Copy, Video } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { SuperAdminDemoBookings } from '@/components/super-admin/SuperAdminDemoBookings';
import { SuperAdminDemoAvailability } from '@/components/super-admin/SuperAdminDemoAvailability';
import { SuperAdminDemoAssign } from '@/components/super-admin/SuperAdminDemoAssign';
import { SuperAdminDemoReport } from '@/components/super-admin/SuperAdminDemoReport';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin "Demos" — availability (Event Types + per-day + calendar), the
// bookings list, and the round-robin Assign page. Event settings live in the
// event card's drawer; the booking link is the card's copy-link icon.
// ────────────────────────────────────────────────────────────────────────────

interface DemoConfig {
  adminId: string;
  eventName: string;
  workingDays: number[];
  weeklyHours: Record<string, { start: string; end: string }[]>;
  startTime: string;
  endTime: string;
  timezone: string;
  slotDuration: number;
  bufferBefore: number;
  bufferAfter: number;
  maxPerDay: number;
  advanceNotice: number;
  futureLimit: number;
  isActive: boolean;
  bookingLink: string;
}

export function SuperAdminDemoSettingsPage() {
  const [cfg, setCfg] = useState<DemoConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'availability' | 'bookings' | 'assign' | 'report'>('availability');

  useEffect(() => {
    saApi.get('/super-admin/demo/config')
      .then(({ data }) => setCfg(data))
      .catch(e => toast('error', extractErrorMessage(e, 'Failed to load demo settings')))
      .finally(() => setLoading(false));
  }, []);

  return (
    <SuperAdminLayout title="Demos">
      {loading || !cfg ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Tabs */}
          <div className="flex gap-2">
            {(['availability', 'bookings', 'assign', 'report'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 h-9 rounded-lg text-sm font-semibold capitalize ${
                  tab === t ? 'bg-[#6366F1] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === 'availability' && <SuperAdminDemoAvailability cfg={cfg} onSaved={(c) => setCfg(c as DemoConfig)} />}
          {tab === 'bookings' && <><ZoomWebhookHelp /><SuperAdminDemoBookings adminId={cfg.adminId} /></>}
          {tab === 'assign' && <SuperAdminDemoAssign />}
          {tab === 'report' && <SuperAdminDemoReport />}
        </div>
      )}
    </SuperAdminLayout>
  );
}

// Collapsible setup help for demo attendance + transcripts. The webhook URL is
// PLATFORM-wide (no tenantId) — different from the vendor URL shown in the
// shared Integrations → Zoom card, which does not apply to demos.
const DEMO_WEBHOOK_URL = 'https://api.brixlead.com/api/webhooks/zoom-demo';

function ZoomWebhookHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/40">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-[#6366F1]" /> : <ChevronRight className="h-4 w-4 text-[#6366F1]" />}
        <Video className="h-4 w-4 text-[#6366F1]" />
        <span className="text-sm font-semibold text-slate-800">Enable attendance &amp; transcripts (optional)</span>
        <span className="text-xs text-slate-400">— one-time Zoom webhook setup</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 text-xs text-slate-600">
          <p>To record who joined, start/end times and the meeting transcript on each demo, add this <b>notification URL</b> in your Zoom app → <b>Event Subscriptions</b>:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md bg-white border border-slate-200 px-2 py-1.5 text-[11px] text-slate-700">{DEMO_WEBHOOK_URL}</code>
            <button onClick={() => { navigator.clipboard.writeText(DEMO_WEBHOOK_URL); toast('success', 'Webhook URL copied'); }}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-medium text-slate-600 hover:bg-slate-50">
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
          <p className="text-[11px] text-amber-700">Note: this is <b>not</b> the <code>/webhooks/zoom/&lt;workspace-id&gt;</code> URL from Settings → Integrations — that one is for vendor meetings and needs a tenant id. For demos use the URL above (no id).</p>
          <ol className="list-decimal ml-4 space-y-1">
            <li>Subscribe to events: <b>Meeting Started</b>, <b>Meeting Ended</b>, <b>Participant/Host Joined</b>, <b>Participant/Host Left</b>, and <b>Recording Transcript Completed</b>.</li>
            <li>Copy the app's <b>Secret Token</b> and paste it into <b>Settings → Integrations → Zoom → Webhook Secret Token</b>, then Save.</li>
            <li>For transcripts, turn on <b>cloud recording + audio transcript</b> on the Zoom host account (meetings are already created with cloud recording on).</li>
          </ol>
        </div>
      )}
    </div>
  );
}
